import { type CSSProperties } from 'typestyle/lib/types';
import DashboardTheme from './dashboard-theme';

export const searchRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
};

export const searchInput: CSSProperties = {
  width: '100%',
  maxWidth: 320,
  padding: '9px 14px',
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  background: DashboardTheme.white,
};

// Without an explicit color these inherit the app shell's default body text color (white, set
// globally on <body>), invisible against the white page/card backgrounds used everywhere here.
export const errorMessage: CSSProperties = {
  display: 'block',
  color: DashboardTheme.red500,
  fontSize: 13.5,
  fontWeight: 600,
  padding: '12px 0',
};

export const emptyMessage: CSSProperties = {
  display: 'block',
  color: DashboardTheme.gray500,
  fontSize: 13.5,
  padding: '12px 0',
};

// Brief guidance shown under every chart: how to zoom/pan/export, and that a toolbar exists at
// all — the modebar (camera/zoom/pan icons) is easy to miss in the top-right corner otherwise.
export const chartHint: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: DashboardTheme.gray500,
  fontSize: 11.5,
  marginTop: 8,
};

export const chartHintIcon: CSSProperties = {
  fontSize: 14,
  color: DashboardTheme.gray300,
  flexShrink: 0,
};

export const pageTitle: CSSProperties = {
  fontFamily: DashboardTheme.headerFontFamily,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: DashboardTheme.navy900,
  margin: '0 0 3px 0',
};

export const pageSub: CSSProperties = {
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.gray700,
  fontSize: 13.5,
  margin: '0 0 4px 0',
};

export const panelLead: CSSProperties = {
  fontFamily: DashboardTheme.headerFontFamily,
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: DashboardTheme.navy900,
  margin: '0 0 3px 0',
};

export const tabBar = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  gap: isMobile ? 14 : 26,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  margin: '14px 0 22px',
  flexWrap: 'wrap',
  fontFamily: DashboardTheme.bodyFontFamily,
});

export const tab = (active: boolean): CSSProperties => ({
  padding: '10px 4px',
  cursor: 'pointer',
  fontSize: 13.5,
  fontWeight: 600,
  color: active ? DashboardTheme.navy800 : DashboardTheme.gray500,
  borderBottom: active ? `2px solid ${DashboardTheme.amber500}` : '2px solid transparent',
  whiteSpace: 'nowrap',
  transition: 'color .12s ease',
});

export const card: CSSProperties = {
  background: DashboardTheme.white,
  borderRadius: DashboardTheme.radius,
  border: `1px solid ${DashboardTheme.gray200}`,
  boxShadow: DashboardTheme.shadow,
  padding: '18px 20px',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const cardTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
};

export const rule: CSSProperties = {
  height: 2,
  width: 26,
  background: DashboardTheme.amber500,
  borderRadius: 2,
  margin: '2px 0 16px',
};

export const kpiCard: CSSProperties = {
  ...card,
  flex: 1,
  minWidth: 150,
};

export const kpiLabel: CSSProperties = {
  fontSize: 12,
  color: DashboardTheme.gray700,
  fontWeight: 500,
  marginBottom: 6,
};

export const kpiValue: CSSProperties = {
  fontSize: 26,
  fontWeight: 700,
  fontFamily: DashboardTheme.headerFontFamily,
  letterSpacing: '-0.01em',
  color: DashboardTheme.navy900,
};

export const kpiValueWarn: CSSProperties = {
  ...kpiValue,
  color: DashboardTheme.red500,
};

export const kpiDelta = (direction: 'up' | 'down' | 'flat'): CSSProperties => {
  const colors: Record<typeof direction, string> = {
    up: DashboardTheme.green600,
    down: DashboardTheme.red500,
    flat: DashboardTheme.gray500,
  };

  return {
    fontSize: 11.5,
    fontWeight: 600,
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    color: colors[direction],
  };
};

export const kpiRow: CSSProperties = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 16,
};

export const officeCard = (isMobile: boolean): CSSProperties => ({
  ...card,
  cursor: 'pointer',
  width: isMobile ? '100%' : undefined,
  transition: 'transform .12s ease, box-shadow .12s ease',
});

export const officeCardHeader: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 12,
};

export const officeName: CSSProperties = {
  fontWeight: 700,
  fontSize: 14.5,
  color: DashboardTheme.navy900,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const officeMeta: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
};

const statusBadgePalette: Record<'quiet' | 'healthy' | 'over', { bg: string; color: string }> = {
  over: { bg: '#fdece9', color: DashboardTheme.red500 },
  quiet: { bg: DashboardTheme.gray100, color: DashboardTheme.gray700 },
  healthy: { bg: DashboardTheme.green100, color: DashboardTheme.green600 },
};

export const statusBadge = (status: 'quiet' | 'healthy' | 'over'): CSSProperties => ({
  fontSize: 10.5,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 999,
  background: statusBadgePalette[status].bg,
  color: statusBadgePalette[status].color,
  whiteSpace: 'nowrap',
});

export const miniStats: CSSProperties = {
  display: 'flex',
  gap: 14,
  marginTop: 12,
  paddingTop: 12,
  borderTop: `1px dashed ${DashboardTheme.gray200}`,
};

export const miniStat: CSSProperties = {
  flex: 1,
};

export const miniStatValue: CSSProperties = {
  display: 'block',
  fontFamily: DashboardTheme.monoFontFamily,
  fontSize: 14,
  fontWeight: 600,
  color: DashboardTheme.navy800,
};

export const miniStatLabel: CSSProperties = {
  fontSize: 10.5,
  color: DashboardTheme.gray500,
};

export const gaugeWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

export const gaugeLegend: CSSProperties = {
  display: 'flex',
  gap: 10,
  fontSize: 10.5,
  color: DashboardTheme.gray700,
  marginTop: 2,
  flexWrap: 'wrap',
  justifyContent: 'center',
};

export const gaugeLegendItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

export const legendDot = (color: string): CSSProperties => ({
  width: 7,
  height: 7,
  borderRadius: 2,
  display: 'inline-block',
  background: color,
});

export const centerText: CSSProperties = {
  textAlign: 'center',
};

export const callout: CSSProperties = {
  border: `1px solid ${DashboardTheme.amber500}`,
  background: DashboardTheme.amber100,
  borderRadius: 12,
  padding: '14px 16px',
  fontSize: 12.8,
  color: DashboardTheme.navy800,
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const chip = (active: boolean): CSSProperties => ({
  padding: '7px 13px',
  borderRadius: 999,
  fontSize: 12.5,
  fontWeight: 600,
  color: active ? DashboardTheme.white : DashboardTheme.navy700,
  background: active ? DashboardTheme.navy800 : DashboardTheme.white,
  border: `1px solid ${active ? DashboardTheme.navy800 : DashboardTheme.gray200}`,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const pillButton = (primary?: boolean): CSSProperties => ({
  border: `1px solid ${primary ? DashboardTheme.navy900 : DashboardTheme.gray200}`,
  background: primary ? DashboardTheme.navy900 : DashboardTheme.white,
  color: primary ? DashboardTheme.white : DashboardTheme.navy700,
  borderRadius: 10,
  padding: '8px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: DashboardTheme.bodyFontFamily,
});
